
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
// import AdditionalServices from '@/modules/InvoiceModule/components/AdditionalServices/index.vue'

// module components
import TemplatesActions from '@/modules/CreateOrderModule/components/TemplatesActions/index.vue'
import StepCargo from '@/modules/CreateOrderModule/components/StepCargo/index.vue'

// realisations
import { orders } from '@/core/realisations/orders.realisation'
import { receivers } from '@/core/realisations/receivers.realisation'

// types
import { InvoiceT } from '@/core/types/orders.types'
import { ReceiverPayloadT, ReceiverT } from '@/core/types/receivers.types'
import { CompletedCargoT } from '@/core/types/create_order.types'
import { AdditionalServicesI, AdditionalServicesLTLI } from '@/core/types/additional_services.types'

// composable
import { useRoute, useRouter } from 'vue-router'
import useAccount from '@/common/composable/useAccount'
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'

// constants
import { EMPTY_ADDITIONAL_SERVICES } from '@/core/constants/additional_services.constants'
import { ADDITIONAL_SERVICES_KEYS } from '@/core/constants/ltl.constants'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        loader: Loader,
        //
        'templates-actions': TemplatesActions,
        'step-cargo': StepCargo,
        // 'additional-services': AdditionalServices,
    },
    setup() {
        const { havePermission } = useAccount()

        const route = useRoute()
        const router = useRouter()

        const invoice_id = Number(route.params.invoice_id)

        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_loaded: false,
            invoice: null as InvoiceT & { receiver: { city_type: number; } } | null,
            updating_cargo: null as CompletedCargoT | null,
            is_receiver_services_loading: false,
            is_sender_services_loading: false,
            additional_services_temp: null as AdditionalServicesLTLI | null,
        })

        const getAdditionalServices = () => {
            if (!state.invoice) return

            const additional_services_values: any = state.invoice.additional_services_values,
                additional_services: any = { ...EMPTY_ADDITIONAL_SERVICES }

            for (const service_name in additional_services) {
                const key = ADDITIONAL_SERVICES_KEYS[service_name],
                    a_s_found = additional_services_values.find((a_s: any) => a_s.type_code === key)

                if (a_s_found) {
                    additional_services[service_name] = true
                }
            }

            return additional_services
        }

        const setCargo = () => {
            if (!state.invoice) return

            const volume_weight =
                (Number(state.invoice.width) *
                    Number(state.invoice.height) *
                    Number(state.invoice.depth)) /
                1000000

            const additional_services = getAdditionalServices()

            const declared_price = state.invoice.declared_price
                ? state.invoice.declared_price.toString()
                : '0'

            const cargo: CompletedCargoT = {
                receiver: state.invoice.receiver,
                logistics_info: {
                    additional_services,
                    product_name: state.invoice.product_name,
                    customer_invoice_number: state.invoice.dop_invoice_number,
                    weight: state.invoice.weight,
                    places: state.invoice.places,
                    depth: state.invoice.depth,
                    width: state.invoice.width,
                    height: state.invoice.height,
                    volume: volume_weight > 0.01 ? Number(volume_weight.toFixed(2)) : 0.01,
                    annotation: state.invoice.annotation,
                    is_return_documents: state.invoice.should_return_document,
                    cod_payment: state.invoice.cod_payment,
                    cargo_name: state.invoice.cargo_name,
                    declared_price,
                    shipment_type: state.invoice.shipment_type_id,
                    payment_type: state.invoice.payment_type_id,
                    payment_method: state.invoice.payment_method_id,
                    payer_company_id: state.invoice.payer_company_id,
                    cash_sum: state.invoice.cash_sum,
                    verify: state.invoice.verify,
                    is_dangerous: state.invoice.is_dangerous,
                    invoice_files: state.invoice.invoice_files,
                    certificate_of_safety_files: state.invoice.certificate_of_safety_files,
                    temperature_regime_safety_files: state.invoice.temperature_regime_safety_files,
                    temperature_regime_type_id: state.invoice.temperature_regime
                        ? state.invoice.temperature_regime.id
                        : null,
                },
            }

            state.updating_cargo = cargo
        }

        const loadInvoice = async () => {
            state.is_loaded = false
            
            try {
                state.is_loading = true

                const response = await orders.getInvoice(invoice_id)
                // @ts-ignore
                state.invoice = response.data

                setCargo()

                state.is_failed = false
                state.is_loaded = true
            } catch (error) {
                console.error(error)

                state.is_loaded = false
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadInvoice)

        const { actions_state, startUpdate } = useActions<ReceiverT>()

        const onReceiverUpdate = async (receiver: ReceiverPayloadT) => {
            if (!state.invoice || !receiver.latitude || !receiver.longitude || !receiver.id) return

            try {
                actions_state.is_actions_loading = true
                await receivers.updateReceiverInOrder(receiver.id, receiver)

                notifications.notify({ title: 'Получатель отредактирован', type: 'success' })

                // Добавлено потому что должен обновиться city_type
                loadInvoice()

                // state.invoice.receiver.city_id = receiver.city_id
                // state.invoice.receiver.index = receiver.index
                // state.invoice.receiver.latitude = receiver.latitude.toString()
                // state.invoice.receiver.longitude = receiver.longitude.toString()
                // state.invoice.receiver.office = receiver.office
                // state.invoice.receiver.phone = receiver.phone
                // state.invoice.receiver.street = receiver.street
                // state.invoice.receiver.title = receiver.title
                // state.invoice.receiver.warehouse_id = receiver.warehouse_id
                // state.invoice.receiver.comment = receiver.comment
                // state.invoice.receiver.entity = receiver.title
                // state.invoice.receiver.full_address = receiver.full_address
                // state.invoice.receiver.full_name = receiver.full_name
                // state.invoice.receiver.house = receiver.house

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при редактировании получателя' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateAdditionalServicesFromCargo = async (
            additional_services: AdditionalServicesI
        ) => {
            if (!state.invoice || !state.invoice.additional_services) return

            let payload: AdditionalServicesLTLI = {
                hasCar: false,
                carCost: 0,
                hasManipulator: false,
                manipulatorCost: 0,
                hasCrane: false,
                craneCost: 0,
                hasHydraulicTrolley: false,
                hydraulicTrolleyCost: 0,
                hasPallet: false,
                palletCost: 0,
                hasLoader: false,
                hoursOfLoadersWork: 0,
                amountOfLoaders: 0,
                hasSoftPackage: false,
                hasGrid: false,
            }

            if (state.additional_services_temp) {
                payload.hasCar = state.additional_services_temp.hasCar
                payload.carCost = state.additional_services_temp.carCost
                payload.hasManipulator = state.additional_services_temp.hasManipulator
                payload.manipulatorCost = state.additional_services_temp.manipulatorCost
                payload.hasCrane = state.additional_services_temp.hasCrane
                payload.craneCost = state.additional_services_temp.craneCost
                payload.hasHydraulicTrolley = state.additional_services_temp.hasHydraulicTrolley
                payload.hydraulicTrolleyCost = state.additional_services_temp.hydraulicTrolleyCost
                payload.hasPallet = state.additional_services_temp.hasPallet
                payload.palletCost = state.additional_services_temp.palletCost
                payload.hasLoader = state.additional_services_temp.hasLoader
                payload.hoursOfLoadersWork = state.additional_services_temp.hoursOfLoadersWork
                payload.amountOfLoaders = state.additional_services_temp.amountOfLoaders
                payload.hasSoftPackage = state.additional_services_temp.hasSoftPackage
                payload.hasGrid = state.additional_services_temp.hasGrid
            }

            payload.hasGrid = additional_services.hasGrid
            payload.hasPallet = additional_services.hasPallet
            payload.hasSoftPackage = additional_services.hasSoftPackage

            try {
                state.is_receiver_services_loading = true

                await receivers.updateAdditionalServices(
                    state.invoice.additional_services.id,
                    payload
                )
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При обновлении доп услуг произошла ошибка',
                })
            } finally {
                state.is_receiver_services_loading = false
            }
        }

        const onCargoUpdated = async ({
            additional_services,
        }: {
            additional_services: AdditionalServicesI
        }) => {
            try {
                await updateAdditionalServicesFromCargo(additional_services)
                router.push(`/invoice/${invoice_id}`)
            } catch (error) {
                console.error(error)
            }
        }

        const setPayload = (additional_services: AdditionalServicesLTLI) => {
            let payload: AdditionalServicesLTLI = {}

            payload.hasCar = additional_services.hasCar
            payload.hasManipulator = additional_services.hasManipulator
            payload.hasCrane = additional_services.hasCrane
            payload.hasHydraulicTrolley = additional_services.hasHydraulicTrolley
            payload.hasPallet = additional_services.hasPallet
            payload.hasLoader = additional_services.hasLoader
            payload.hasSoftPackage = additional_services.hasSoftPackage
            payload.hasGrid = additional_services.hasGrid

            if (additional_services.hasCar) {
                payload.carCost = additional_services.carCost || 0
            }

            if (additional_services.hasManipulator) {
                payload.manipulatorCost = additional_services.manipulatorCost || 0
            }

            if (additional_services.hasCrane) {
                payload.craneCost = additional_services.craneCost || 0
            }

            if (additional_services.hasHydraulicTrolley) {
                payload.hydraulicTrolleyCost = additional_services.hydraulicTrolleyCost || 0
            }

            if (additional_services.hasPallet) {
                payload.palletCost = additional_services.palletCost || 0
            }

            if (additional_services.hasLoader) {
                payload.amountOfLoaders = additional_services.amountOfLoaders || 0
                payload.hoursOfLoadersWork = additional_services.hoursOfLoadersWork || 0
            }

            return payload
        }

        const onAdditionalServicesSubmit = async (additional_services: AdditionalServicesLTLI) => {
            try {
                if (!state.invoice || !state.invoice.additional_services) return
                state.is_receiver_services_loading = true

                await receivers.updateAdditionalServices(
                    state.invoice.additional_services.id,
                    setPayload(additional_services)
                )

                state.additional_services_temp = { ...additional_services }

                notifications.notify({
                    title: 'Дополнительные услуги получателя отредактированы',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'Ошибка при редактировании дополнительных услуг получателя',
                })
            } finally {
                state.is_receiver_services_loading = false
            }
        }

        const onSenderAdditionalServicesSubmit = async (
            additional_services: AdditionalServicesLTLI
        ) => {
            try {
                if (!state.invoice || !state.invoice.order_additional_services) return
                state.is_sender_services_loading = true

                await receivers.updateAdditionalServices(
                    state.invoice.order_additional_services.id,
                    setPayload(additional_services)
                )

                notifications.notify({
                    title: 'Дополнительные услуги отредактированы отправителя',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'Ошибка при редактировании дополнительных услуг отправителя',
                })
            } finally {
                state.is_sender_services_loading = false
            }
        }

        // До обработки
        const isOnBeforeHandling = computed(() => {
            if (!state.invoice) return false
            if (havePermission('all-root-permission')) return true
            if (!state.invoice.status) return false

            if (
                state.invoice.status.code === 201 ||
                state.invoice.status.code === 202 ||
                state.invoice.status.code === 203 ||
                state.invoice.status.code === 225
            ) {
                return true
            }

            return false
        })

        const isOnBeforeGivingOnDelivery = computed(() => {
            if (!state.invoice) return false
            if (havePermission('all-root-permission')) return true
            if (!state.invoice.status) return false

            if (
                state.invoice.status.code === 201 ||
                state.invoice.status.code === 202 ||
                state.invoice.status.code === 203 ||
                state.invoice.status.code === 225 ||
                state.invoice.status.code === 205 ||
                state.invoice.status.code === 206 ||
                state.invoice.status.code === 208
            ) {
                return true
            }

            return false
        })

        return {
            invoice_id,
            state,
            loadInvoice,
            actions_state,
            startUpdate,
            onReceiverUpdate,
            onCargoUpdated,
            onAdditionalServicesSubmit,
            onSenderAdditionalServicesSubmit,
            isOnBeforeHandling,
            isOnBeforeGivingOnDelivery,
        }
    },
})
